/**
 * FieldRow — one mapping edge as a grid row (canonical | transform | vendor |
 * confidence | status), with an expandable resolve panel (candidates + schema
 * search + remember + revert). Verbatim grid + recipe.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { ChevronDown, Sparkles, Check } from '../../icons';
import { ConfidenceBar, SearchInput } from '../../components';
import { useBridge } from '../../store/bridgeStore';
import Svg, { Path } from 'react-native-svg';

const GRID = 'minmax(0,1fr) 116px minmax(0,1fr) 96px 132px';

export default function FieldRow({ f, vendorName, schemaLabel }: { f: any; vendorName: string; schemaLabel: string }) {
  const { state, actions } = useBridge();

  return (
    <View style={[styles.wrap, { borderColor: f.rowBorder, borderLeftColor: f.rowStripe, backgroundColor: f.rowBg } as any]}>
      {React.createElement(
        'div',
        { style: { display: 'grid', gridTemplateColumns: GRID, gap: 14, alignItems: 'center', padding: '12px 16px' } },
        // canonical
        <View style={{ minWidth: 0 }} key="c">
          <Text className="t-mono" style={styles.fieldName} numberOfLines={1}>{f.canonName}</Text>
          <Text style={styles.fieldType} numberOfLines={1}>{f.canonType}</Text>
        </View>,
        // transform
        <View style={styles.transformCol} key="t">
          <Text className="t-mono" style={styles.via} numberOfLines={1}>{f.via}</Text>
          <Svg width={34} height={8} viewBox="0 0 34 8" fill="none" style={{ transform: [{ rotate: f.arrowRotate }] } as any}>
            <Path d="M0 4h28" stroke={f.arrowColor} strokeWidth={1.5} />
            <Path d="m27 1 5 3-5 3" stroke={f.arrowColor} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        </View>,
        // vendor
        <View style={{ minWidth: 0 }} key="v">
          <Text className="t-mono" style={styles.fieldName} numberOfLines={1}>{f.vendName}</Text>
          <Text style={styles.fieldType} numberOfLines={1}>{f.vendType}</Text>
        </View>,
        // confidence
        <View key="conf">
          <ConfidenceBar width={f.confWidth} color={f.confColor} />
          <Text className="t-mono" style={styles.confText}>{f.conf}</Text>
        </View>,
        // status / resolve
        <View style={styles.statusCol} key="s">
          {f.isNeeds ? (
            <Pressable onPress={() => actions.toggleField(f.fid)} style={[styles.resolveBtn, f.expanded ? { backgroundColor: colors.warn } : { backgroundColor: colors.warnSoft }]}>
              <Text style={[styles.resolveText, { color: f.expanded ? '#fff' : colors.warn } as any]}>{f.expanded ? 'Close' : 'Resolve'}</Text>
              <ChevronDown size={11} color={f.expanded ? '#fff' : colors.warn} />
            </Pressable>
          ) : (
            <Pressable
              onPress={() => actions.toggleField(f.fid)}
              style={[styles.statusPill, { borderColor: `var(--${f.statusTone}-line)`, backgroundColor: `var(--${f.statusTone}-soft)` } as any]}
            >
              {f.statusIcon === 'ai' && <Sparkles size={11} color={`var(--${f.statusTone})` as any} />}
              {f.statusIcon === 'check' && <Check size={11} color={`var(--${f.statusTone})` as any} />}
              <Text style={[styles.statusText, { color: `var(--${f.statusTone})` } as any]}>{f.statusLabel}</Text>
              <ChevronDown size={10} color={`var(--${f.statusTone})` as any} />
            </Pressable>
          )}
        </View>,
      )}

      {f.expanded && (
        <View style={[styles.panel, { borderTopColor: f.panelBorder, backgroundColor: f.panelBg } as any]}>
          <Text style={styles.panelDesc}>{f.discDesc}</Text>

          <Text style={styles.panelHeading}>{f.candidatesHeading}</Text>
          <View style={{ gap: 6 }}>
            {f.candidates.map((c: any, i: number) => (
              <Pressable
                key={i}
                onPress={() => actions.pickTarget(f.fid, f.disc, c.label)}
                style={[styles.candidate, { borderColor: c.isCurrent ? colors.accentLine : colors.line } as any]}
              >
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text className="t-mono" style={styles.candidateLabel}>{c.label}</Text>
                  <Text style={styles.candidateNote}>{c.note}</Text>
                </View>
                {c.isCurrent && <Text style={styles.current}>current</Text>}
                <Text className="t-mono" style={styles.candidateConf}>{c.conf}</Text>
              </Pressable>
            ))}
          </View>

          <Text style={[styles.panelHeading, { marginTop: 14 }]}>Or map to any field in the schema</Text>
          <SearchInput value={state.fieldSearch} onChange={actions.setFieldSearch} placeholder={'Search ' + schemaLabel + '…'} mono height={36} />
          {f.hasSearch && (
            <View style={styles.searchResults}>
              {f.searchResults.map((r: any, i: number) => (
                <Pressable key={i} onPress={() => actions.pickTarget(f.fid, f.disc, r.label, { manual: true })} style={styles.searchRow}>
                  <Text className="t-mono" style={[styles.searchLabel, { flex: 1 }]}>{r.label}</Text>
                  <Text style={styles.searchType}>{r.type}</Text>
                  {r.isCurrent && <Text style={styles.current}>current</Text>}
                </Pressable>
              ))}
            </View>
          )}
          {f.searchEmpty && (
            <Text style={styles.searchEmpty}>No field matches — the schema is fixed, so the mapping must target an existing field.</Text>
          )}

          <View style={styles.panelFooter}>
            {f.showRemember && (
              <Pressable onPress={() => actions.toggleRemember(f.disc)} style={styles.remember}>
                <View style={[styles.checkbox, f.remember ? { backgroundColor: colors.accent, borderColor: colors.accent } : null]}>
                  {f.remember && <Check size={10} color="#fff" />}
                </View>
                <Text style={styles.rememberText}>{'Remember for ' + vendorName}</Text>
              </Pressable>
            )}
            <View style={{ flex: 1 }} />
            {f.canReset && (
              <Pressable onPress={() => actions.resetField(f.fid, f.disc)} style={styles.revert}>
                <Text style={styles.revertText}>Revert to AI</Text>
              </Pressable>
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { borderWidth: 1, borderLeftWidth: 3, borderRadius: radii.r3, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' } as any,
  fieldName: { fontFamily: fontFamily.mono, fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink },
  fieldType: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  transformCol: { alignItems: 'center', gap: 4 },
  via: { fontFamily: fontFamily.mono, fontSize: 11, color: colors.ink2, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, paddingVertical: 2, paddingHorizontal: 9, maxWidth: '100%' } as any,
  confText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3, marginTop: 4 },
  statusCol: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 6 },
  resolveBtn: { height: 30, paddingHorizontal: 12, borderRadius: radii.r2, borderWidth: 1, borderColor: colors.warn, flexDirection: 'row', alignItems: 'center', gap: 5, cursor: 'pointer' } as any,
  resolveText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, fontSize: fontSize.sm },
  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 28, paddingHorizontal: 10, borderRadius: radii.full, borderWidth: 1, cursor: 'pointer' } as any,
  statusText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium },

  panel: { borderTopWidth: 1, padding: 14, paddingHorizontal: 16 },
  panelDesc: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, lineHeight: 19.5, marginBottom: 12 },
  panelHeading: { fontFamily: fontFamily.sans, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold, marginBottom: 7 },
  candidate: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderWidth: 1, borderRadius: radii.r2, backgroundColor: colors.card, cursor: 'pointer' } as any,
  candidateLabel: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink },
  candidateNote: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  current: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.accent, fontWeight: fontWeight.medium },
  candidateConf: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink2, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, paddingVertical: 2, paddingHorizontal: 8 },
  searchResults: { gap: 4, marginTop: 8, maxHeight: 188, overflow: 'scroll' } as any,
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.card, cursor: 'pointer' } as any,
  searchLabel: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },
  searchType: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  searchEmpty: { marginTop: 8, fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3, paddingVertical: 8, paddingHorizontal: 11 },
  panelFooter: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 13 },
  remember: { flexDirection: 'row', alignItems: 'center', gap: 8, cursor: 'pointer' } as any,
  checkbox: { width: 14, height: 14, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 3, alignItems: 'center', justifyContent: 'center' },
  rememberText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2 },
  revert: { height: 28, paddingHorizontal: 11, borderRadius: radii.r2, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, justifyContent: 'center', cursor: 'pointer' } as any,
  revertText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2 },
});
